import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { X, Coffee, DollarSign, Clock, Plus, Minus, ChefHat, Star, Flame, Award, Leaf, Settings } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { dataService, RawMaterial, AddOnCategory, MenuItemAddOns, MenuItem } from '../../services/dataService';
import { useCategories } from '../../hooks/useCategories';
import { uploadMenuItemImage, validateMenuItemImage } from '../../services/menuItemImageService';

interface AddMenuItemModalProps {
  onClose: () => void;
  onSuccess: () => void;
}

interface Ingredient {
  rawMaterialId: string;
  quantity: number;
  unit: string;
}

const AddMenuItemModal: React.FC<AddMenuItemModalProps> = ({ onClose, onSuccess }) => {
  const { tenantData } = useAuth();
  const { getCategoryNames } = useCategories('menuItem');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [rawMaterials, setRawMaterials] = useState<RawMaterial[]>([]);
  
  const categories = getCategoryNames();
  
  const [formData, setFormData] = useState({
    name: '',
    category: categories.length > 0 ? categories[0] : '',
    price: 0,
    description: '',
    preparationTime: 5,
    isAvailable: true,
    autoDeductInventory: true,
    isFeatured: false,
    isSpecialOffer: false,
    isTodaysSpecial: false,
    originalPrice: 0,
    spiceLevel: 0 as 0 | 1 | 2 | 3 | 4 | 5,
    dietaryInfo: [] as string[],
    badges: [] as string[],
    tags: [] as string[]
  });

  const [ingredients, setIngredients] = useState<Ingredient[]>([]);

  // Add-ons configuration state
  const [addOnCategories, setAddOnCategories] = useState<AddOnCategory[]>([]);
  const [selectedAddOnCategories, setSelectedAddOnCategories] = useState<Array<{
    categoryId: string;
    categoryName: string;
    isRequired: boolean;
    minSelections: number;
    maxSelections: number;
    displayOrder: number;
  }>>([]);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreviewUrl, setImagePreviewUrl] = useState<string>('');

  // Lock body scroll when modal is open
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, []);

  useEffect(() => {
    return () => {
      if (imagePreviewUrl.startsWith('blob:')) {
        URL.revokeObjectURL(imagePreviewUrl);
      }
    };
  }, [imagePreviewUrl]);

  // Update category when categories load
  useEffect(() => {
    if (categories.length > 0 && !formData.category) {
      setFormData(prev => ({ ...prev, category: categories[0] }));
    }
  }, [categories, formData.category]);

  const loadRawMaterials = useCallback(async () => {
    try {
      const materials = await dataService.getRawMaterials(tenantData!.id);
      setRawMaterials(materials);
    } catch (error) {
      console.error('Error loading raw materials:', error);
    }
  }, [tenantData]);

  useEffect(() => {
    if (tenantData?.id) {
      loadRawMaterials();
      loadAddOnCategories();
    }
  }, [tenantData, loadRawMaterials]);

  const loadAddOnCategories = useCallback(async () => {
    try {
      const categories = await dataService.getAddOnCategories(tenantData!.id);
      setAddOnCategories(categories);
    } catch (error) {
      console.error('Error loading add-on categories:', error);
    }
  }, [tenantData]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'number' ? parseFloat(value) || 0 : 
               type === 'checkbox' ? (e.target as HTMLInputElement).checked : value
    }));
  };

  const addIngredient = () => {
    setIngredients(prev => [...prev, {
      rawMaterialId: '',
      quantity: 0,
      unit: 'kg'
    }]);
  };

  const removeIngredient = (index: number) => {
    setIngredients(prev => prev.filter((_, i) => i !== index));
  };

  const updateIngredient = (index: number, field: keyof Ingredient, value: string | number) => {
    setIngredients(prev => prev.map((ingredient, i) => 
      i === index ? { ...ingredient, [field]: value } : ingredient
    ));
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) {
      return;
    }

    const validationError = validateMenuItemImage(file);
    if (validationError) {
      setError(validationError);
      e.target.value = '';
      return;
    }

    if (imagePreviewUrl.startsWith('blob:')) {
      URL.revokeObjectURL(imagePreviewUrl);
    }

    setError('');
    setImageFile(file);
    setImagePreviewUrl(URL.createObjectURL(file));
  };

  const clearImageSelection = () => {
    if (imagePreviewUrl.startsWith('blob:')) {
      URL.revokeObjectURL(imagePreviewUrl);
    }
    setImageFile(null);
    setImagePreviewUrl('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      setError('Please enter a menu item name');
      return;
    }

    if (formData.price <= 0) {
      setError('Price must be greater than 0');
      return;
    }

    if (formData.preparationTime <= 0) {
      setError('Preparation time must be greater than 0');
      return;
    }

    try {
      setLoading(true);
      setError('');

      const menuItemData = {
        name: formData.name.trim(),
        category: formData.category.trim(),
        price: parseFloat(formData.price.toString()),
        description: formData.description.trim() || undefined,
        ingredients: ingredients
          .filter(ing => ing.rawMaterialId && ing.quantity > 0)
          .map((ing: Ingredient) => {
            const material = rawMaterials.find(m => m.id === ing.rawMaterialId);
            if (!material) return ing;
            
            // Convert quantity to base units (sub-units) for consistent storage
            let baseQuantity = ing.quantity;
            
            // If using main unit, convert to sub-units
            if (ing.unit === material.mainUnit && 
                material.subUnitsPerMainUnit && 
                material.subUnitsPerMainUnit > 1) {
              baseQuantity = ing.quantity * material.subUnitsPerMainUnit;
            }
            
            return {
              rawMaterialId: ing.rawMaterialId,
              quantity: baseQuantity,
              unit: material.subUnit || material.baseUnit, // Always store in base/sub units
              originalQuantity: ing.quantity, // Keep original for reference
              originalUnit: ing.unit // Keep original unit for reference
            };
          }),
        preparationTime: parseInt(formData.preparationTime.toString()),
        isAvailable: formData.isAvailable,
        autoDeductInventory: formData.autoDeductInventory,
        saleUnit: 'piece', // Default sale unit
        saleUnitConversion: 1, // Default conversion (1:1)
        
        // Enhanced fields
        isFeatured: formData.isFeatured,
        isSpecialOffer: formData.isSpecialOffer,
        isTodaysSpecial: formData.isTodaysSpecial,
        originalPrice: formData.isSpecialOffer && formData.originalPrice > 0 ? formData.originalPrice : undefined,
        spiceLevel: formData.spiceLevel > 0 ? (formData.spiceLevel as 1 | 2 | 3 | 4 | 5) : undefined,
        dietaryInfo: formData.dietaryInfo.length > 0 ? formData.dietaryInfo : undefined,
        badges: formData.badges.length > 0 ? formData.badges : undefined,
        tags: formData.tags.length > 0 ? formData.tags : undefined
      };

      const menuItemId = await dataService.createMenuItem(tenantData!.id, menuItemData);

      if (imageFile) {
        const imageUrl = await uploadMenuItemImage(tenantData!.id, menuItemId, imageFile);
        await dataService.update<MenuItem>(tenantData!.id, 'menuItems', menuItemId, { image: imageUrl });
      }
      
      // Configure add-ons if any are selected
      if (selectedAddOnCategories.length > 0) {
        try {
          await dataService.configureMenuItemAddOns(tenantData!.id, menuItemId, selectedAddOnCategories);
        } catch (error) {
          console.error('Error configuring add-ons:', error);
          // Don't fail the entire operation if add-ons configuration fails
        }
      }
      
      onSuccess();
    } catch (error) {
      console.error('Error creating menu item:', error);
      setError('Failed to create menu item. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const modalContent = (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[9999]">
      <div className="bg-white/95 dark:bg-gray-800/95 backdrop-blur-md rounded-lg shadow-xl w-full max-w-4xl h-full md:h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-xl tablet:text-2xl font-bold text-gray-900 dark:text-white">
            Add Menu Item
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {error && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
              <p className="text-red-700 dark:text-red-300">{error}</p>
            </div>
          )}

          {/* Basic Information */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Basic Information</h3>
            
            <div className="grid grid-cols-1 tablet:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Item Name *
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Coffee className="h-5 w-5 text-gray-400 dark:text-gray-500" />
                  </div>
                  <input
                    type="text"
                    name="name"
                    required
                    value={formData.name}
                    onChange={handleInputChange}
                    className="input-field pl-10"
                    placeholder="e.g., Cappuccino"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Category *
                </label>
                <select
                  name="category"
                  value={formData.category}
                  onChange={handleInputChange}
                  className="input-field"
                >
                  {categories.map(category => (
                    <option key={category} value={category}>
                      {category}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Description
              </label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                rows={3}
                className="input-field"
                placeholder="Brief description of the menu item"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Item Image
              </label>
              <input
                type="file"
                accept="image/jpeg,image/png,image/webp"
                onChange={handleImageChange}
                className="input-field"
              />
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                Upload JPG, PNG, or WEBP up to 5MB.
              </p>
              {imagePreviewUrl && (
                <div className="mt-3 flex items-center gap-3">
                  <img
                    src={imagePreviewUrl}
                    alt="Menu item preview"
                    className="w-20 h-20 object-cover rounded-lg border border-gray-200 dark:border-gray-700"
                  />
                  <button
                    type="button"
                    onClick={clearImageSelection}
                    className="text-sm text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300"
                  >
                    Remove image
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Pricing & Time */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Pricing & Preparation</h3>
            
            <div className="grid grid-cols-1 tablet:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Price *
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <DollarSign className="h-5 w-5 text-gray-400 dark:text-gray-500" />
                  </div>
                  <input
                    type="number"
                    name="price"
                    required
                    min="0"
                    step="0.01"
                    value={formData.price}
                    onChange={handleInputChange}
                    className="input-field pl-10"
                    placeholder="0.00"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Preparation Time (minutes) *
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Clock className="h-5 w-5 text-gray-400 dark:text-gray-500" />
                  </div>
                  <input
                    type="number"
                    name="preparationTime"
                    required
                    min="1"
                    value={formData.preparationTime}
                    onChange={handleInputChange}
                    className="input-field pl-10"
                    placeholder="5"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Availability */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Availability</h3>
            
            <div className="flex items-center">
              <input
                type="checkbox"
                name="isAvailable"
                checked={formData.isAvailable}
                onChange={handleInputChange}
                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 dark:border-gray-600 rounded"
              />
              <label className="ml-2 block text-sm text-gray-900 dark:text-white">
                Available for ordering
              </label>
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                name="autoDeductInventory"
                checked={formData.autoDeductInventory}
                onChange={handleInputChange}
                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 dark:border-gray-600 rounded"
              />
              <label className="ml-2 block text-sm text-gray-900 dark:text-white">
                Auto-deduct ingredients from inventory when sold
              </label>
            </div>
          </div>

          {/* Enhanced Features Section */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center space-x-2">
              <Star className="w-5 h-5 text-amber-500 dark:text-amber-400" />
              <span>Special Features</span>
            </h3>
            
            <div className="grid grid-cols-1 tablet:grid-cols-2 gap-4">
              {/* Featured Item */}
              <div className="flex items-center">
                <input
                  type="checkbox"
                  name="isFeatured"
                  checked={formData.isFeatured}
                  onChange={handleInputChange}
                  className="h-4 w-4 text-amber-600 focus:ring-amber-500 border-gray-300 dark:border-gray-600 rounded"
                />
                <label className="ml-2 block text-sm text-gray-900 dark:text-white">
                  <span className="font-medium">Featured Item</span>
                  <span className="block text-xs text-gray-500 dark:text-gray-400">Highlight this item on the menu</span>
                </label>
              </div>

              {/* Special Offer */}
              <div className="flex items-center">
                <input
                  type="checkbox"
                  name="isSpecialOffer"
                  checked={formData.isSpecialOffer}
                  onChange={handleInputChange}
                  className="h-4 w-4 text-red-600 focus:ring-red-500 border-gray-300 dark:border-gray-600 rounded"
                />
                <label className="ml-2 block text-sm text-gray-900 dark:text-white">
                  <span className="font-medium">Special Offer</span>
                  <span className="block text-xs text-gray-500 dark:text-gray-400">Mark as special offer/discount</span>
                </label>
              </div>

              {/* Today's Special */}
              <div className="flex items-center">
                <input
                  type="checkbox"
                  name="isTodaysSpecial"
                  checked={formData.isTodaysSpecial}
                  onChange={handleInputChange}
                  className="h-4 w-4 text-yellow-600 focus:ring-yellow-500 border-gray-300 dark:border-gray-600 rounded"
                />
                <label className="ml-2 block text-sm text-gray-900 dark:text-white">
                  <span className="font-medium">Today's Special</span>
                  <span className="block text-xs text-gray-500 dark:text-gray-400">Mark as today's special with star badge</span>
                </label>
              </div>
            </div>

            {/* Original Price (only if special offer) */}
            {formData.isSpecialOffer && (
              <div>
                <label htmlFor="originalPrice" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Original Price (₹) <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  id="originalPrice"
                  name="originalPrice"
                  value={formData.originalPrice}
                  onChange={handleInputChange}
                  min="0"
                  step="0.01"
                  className="input-field"
                  placeholder="Enter original price"
                />
                {formData.originalPrice > 0 && formData.price > 0 && formData.originalPrice > formData.price && (
                  <p className="text-sm text-green-600 dark:text-green-400 mt-1">
                    Discount: ₹{(formData.originalPrice - formData.price).toFixed(2)} 
                    ({(((formData.originalPrice - formData.price) / formData.originalPrice) * 100).toFixed(1)}% off)
                  </p>
                )}
              </div>
            )}

            {/* Spice Level */}
            <div>
              <label htmlFor="spiceLevel" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Spice Level
              </label>
              <div className="flex items-center space-x-4">
                {[0, 1, 2, 3, 4, 5].map((level) => (
                  <label key={level} className="flex items-center">
                    <input
                      type="radio"
                      name="spiceLevel"
                      value={level}
                      checked={formData.spiceLevel === level}
                      onChange={(e) => setFormData(prev => ({ ...prev, spiceLevel: parseInt(e.target.value) as 0 | 1 | 2 | 3 | 4 | 5 }))}
                      className="hidden"
                    />
                    <div className={`flex items-center space-x-1 px-3 py-2 rounded-lg cursor-pointer transition-colors ${
                      formData.spiceLevel === level ? 'bg-red-100 border-2 border-red-500 dark:bg-red-900/20 dark:border-red-800' : 'bg-gray-100 border-2 border-transparent hover:bg-gray-200 dark:hover:bg-gray-700'
                    }`}>
                      {level === 0 ? (
                        <span className="text-sm font-medium text-gray-600 dark:text-gray-400">None</span>
                      ) : (
                        <>
                          {[...Array(level)].map((_, i) => (
                            <Flame key={i} className="w-4 h-4 text-red-500 dark:text-red-400" />
                          ))}
                        </>
                      )}
                    </div>
                  </label>
                ))}
              </div>
            </div>

            {/* Dietary Information */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Dietary Information
              </label>
              <div className="grid grid-cols-2 tablet:grid-cols-4 gap-2">
                {['Vegetarian', 'Vegan', 'Gluten-Free', 'Dairy-Free', 'Nut-Free', 'Halal', 'Kosher', 'Organic'].map((diet) => (
                  <label key={diet} className="flex items-center">
                    <input
                      type="checkbox"
                      checked={formData.dietaryInfo.includes(diet)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setFormData(prev => ({ ...prev, dietaryInfo: [...prev.dietaryInfo, diet] }));
                        } else {
                          setFormData(prev => ({ ...prev, dietaryInfo: prev.dietaryInfo.filter(d => d !== diet) }));
                        }
                      }}
                      className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 dark:border-gray-600 rounded"
                    />
                    <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">{diet}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Custom Badges */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Custom Badges
              </label>
              <div className="grid grid-cols-2 tablet:grid-cols-3 gap-2">
                {['Chef\'s Special', 'New', 'Bestseller', 'Limited Time', 'House Favorite', 'Must Try'].map((badge) => (
                  <label key={badge} className="flex items-center">
                    <input
                      type="checkbox"
                      checked={formData.badges.includes(badge)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setFormData(prev => ({ ...prev, badges: [...prev.badges, badge] }));
                        } else {
                          setFormData(prev => ({ ...prev, badges: prev.badges.filter(b => b !== badge) }));
                        }
                      }}
                      className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 dark:border-gray-600 rounded"
                    />
                    <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">{badge}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>

          {/* Ingredients */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Ingredients</h3>
              <button
                type="button"
                onClick={addIngredient}
                className="btn-secondary text-sm"
              >
                <Plus className="w-4 h-4 mr-1" />
                Add Ingredient
              </button>
            </div>

            {ingredients.length === 0 ? (
              <div className="text-center py-8 bg-gray-50 dark:bg-gray-900 rounded-lg">
                <ChefHat className="w-12 h-12 text-gray-400 dark:text-gray-500 mx-auto mb-2" />
                <p className="text-gray-600 dark:text-gray-400">No ingredients added yet</p>
                <p className="text-sm text-gray-500 dark:text-gray-400">Add ingredients to track inventory usage</p>
              </div>
            ) : (
              <div className="space-y-3">
                {ingredients.map((ingredient, index) => {
                  const selectedMaterial = rawMaterials.find(m => m.id === ingredient.rawMaterialId);
                  
                  return (
                    <div key={index} className="p-4 bg-gray-50 dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700">
                      {/* Material Selection */}
                      <div className="grid grid-cols-1 tablet:grid-cols-3 gap-3 mb-3">
                        <div className="tablet:col-span-2">
                          <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Raw Material
                          </label>
                          <select
                            value={ingredient.rawMaterialId}
                            onChange={(e) => {
                              const selectedMaterial = rawMaterials.find(m => m.id === e.target.value);
                              updateIngredient(index, 'rawMaterialId', e.target.value);
                              if (selectedMaterial) {
                                // Default to sub-unit for recipes
                                updateIngredient(index, 'unit', selectedMaterial.subUnit || selectedMaterial.baseUnit);
                              }
                            }}
                            className="input-field text-sm"
                          >
                            <option value="">Select raw material</option>
                            {rawMaterials.map(material => (
                              <option key={material.id} value={material.id}>
                                {material.name}
                              </option>
                            ))}
                          </select>
                        </div>
                        
                        <div className="flex justify-end">
                          <button
                            type="button"
                            onClick={() => removeIngredient(index)}
                            className="text-red-600 hover:text-red-700 p-2 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                          >
                            <Minus className="w-4 h-4" />
                          </button>
                        </div>
                      </div>

                      {selectedMaterial && (
                        <>
                          {/* Quantity and Unit Selection */}
                          <div className="grid grid-cols-2 gap-3 mb-3">
                            <div>
                              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                                Quantity
                              </label>
                              <input
                                type="number"
                                min="0"
                                step="0.01"
                                value={ingredient.quantity}
                                onChange={(e) => updateIngredient(index, 'quantity', parseFloat(e.target.value) || 0)}
                                className="input-field text-sm"
                                placeholder="0"
                              />
                            </div>
                            
                            <div>
                              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                                Unit
                              </label>
                              <select
                                value={ingredient.unit}
                                onChange={(e) => updateIngredient(index, 'unit', e.target.value)}
                                className="input-field text-sm"
                              >
                                {(() => {
                                  const units = [];
                                  
                                  // Add sub-unit (primary selling unit)
                                  if (selectedMaterial.subUnit) {
                                    units.push({
                                      value: selectedMaterial.subUnit,
                                      label: selectedMaterial.subUnit,
                                      isSubUnit: true
                                    });
                                  }
                                  
                                  // Add main unit if different from sub-unit and has conversion
                                  if (selectedMaterial.mainUnit && 
                                      selectedMaterial.mainUnit !== selectedMaterial.subUnit &&
                                      selectedMaterial.subUnitsPerMainUnit && 
                                      selectedMaterial.subUnitsPerMainUnit > 1) {
                                    units.push({
                                      value: selectedMaterial.mainUnit,
                                      label: `${selectedMaterial.mainUnit} (${selectedMaterial.subUnitsPerMainUnit} ${selectedMaterial.subUnit}s)`,
                                      isSubUnit: false
                                    });
                                  }
                                  
                                  // Fallback to base unit if no sub-unit system
                                  if (units.length === 0 && selectedMaterial.baseUnit) {
                                    units.push({
                                      value: selectedMaterial.baseUnit,
                                      label: selectedMaterial.baseUnit,
                                      isSubUnit: true
                                    });
                                  }
                                  
                                  return units.map(unit => (
                                    <option key={unit.value} value={unit.value}>
                                      {unit.label}
                                    </option>
                                  ));
                                })()}
                              </select>
                            </div>
                          </div>

                          {/* Cost and Conversion Information */}
                          {ingredient.quantity > 0 && ingredient.unit && (
                            <div className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-md rounded-lg p-3 border border-gray-200 dark:border-gray-600">
                              <div className="grid grid-cols-1 tablet:grid-cols-2 gap-3 text-xs">
                                <div>
                                  <span className="font-medium text-gray-700 dark:text-gray-300">Cost per {ingredient.unit}:</span>
                                  <span className="ml-2 text-gray-900 dark:text-white">
                                    ${(() => {
                                      if (ingredient.unit === selectedMaterial.subUnit) {
                                        return (selectedMaterial.costPerSubUnit || selectedMaterial.costPerUnit || 0).toFixed(4);
                                      } else if (ingredient.unit === selectedMaterial.mainUnit) {
                                        return (selectedMaterial.costPerMainUnit || 0).toFixed(2);
                                      }
                                      return (selectedMaterial.costPerUnit || 0).toFixed(4);
                                    })()}
                                  </span>
                                </div>
                                
                                <div>
                                  <span className="font-medium text-gray-700 dark:text-gray-300">Total cost:</span>
                                  <span className="ml-2 text-green-600 dark:text-green-400 font-semibold">
                                    ${(() => {
                                      let costPerUnit = 0;
                                      if (ingredient.unit === selectedMaterial.subUnit) {
                                        costPerUnit = selectedMaterial.costPerSubUnit || selectedMaterial.costPerUnit || 0;
                                      } else if (ingredient.unit === selectedMaterial.mainUnit) {
                                        costPerUnit = selectedMaterial.costPerMainUnit || 0;
                                      } else {
                                        costPerUnit = selectedMaterial.costPerUnit || 0;
                                      }
                                      return (costPerUnit * ingredient.quantity).toFixed(4);
                                    })()}
                                  </span>
                                </div>
                              </div>
                              
                              {/* Conversion Information */}
                              {selectedMaterial.subUnit && 
                               selectedMaterial.mainUnit && 
                               selectedMaterial.subUnitsPerMainUnit && 
                               selectedMaterial.subUnitsPerMainUnit > 1 && (
                                <div className="mt-2 pt-2 border-t border-gray-200 dark:border-gray-700">
                                  <div className="text-xs text-gray-600 dark:text-gray-400">
                                    <strong>Conversion:</strong> 1 {selectedMaterial.mainUnit} = {selectedMaterial.subUnitsPerMainUnit} {selectedMaterial.subUnit}s
                                    {ingredient.unit === selectedMaterial.mainUnit && ingredient.quantity > 0 && (
                                      <span className="ml-2 text-blue-600">
                                        ({ingredient.quantity} {selectedMaterial.mainUnit}s = {ingredient.quantity * selectedMaterial.subUnitsPerMainUnit} {selectedMaterial.subUnit}s)
                                      </span>
                                    )}
                                  </div>
                                </div>
                              )}
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Add-Ons Configuration */}
          <div className="space-y-4 pt-6 border-t border-gray-200 dark:border-gray-700">
            <div className="flex items-center space-x-2">
              <Settings className="w-5 h-5 text-blue-600 dark:text-blue-400" />
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Add-Ons Configuration</h3>
            </div>
            
            <div className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              Configure which add-on categories customers can select from when ordering this item.
            </div>

            {addOnCategories.length === 0 ? (
              <div className="text-center py-6 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
                <Settings className="w-12 h-12 text-gray-400 dark:text-gray-500 mx-auto mb-3" />
                <p className="text-gray-600 dark:text-gray-400 mb-2">No add-on categories available</p>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Create add-on categories first in the Add-On Categories section
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {addOnCategories.map((category) => {
                  const isSelected = selectedAddOnCategories.some(selected => selected.categoryId === category.id);
                  const selectedCategory = selectedAddOnCategories.find(selected => selected.categoryId === category.id);
                  
                  return (
                    <div key={category.id} className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                      <div className="flex items-start space-x-3">
                        <input
                          type="checkbox"
                          id={`category-${category.id}`}
                          checked={isSelected}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedAddOnCategories(prev => [...prev, {
                                categoryId: category.id!,
                                categoryName: category.name,
                                isRequired: category.isRequired || false,
                                minSelections: category.minSelections || 0,
                                maxSelections: category.maxSelections || 0,
                                displayOrder: category.displayOrder || 0
                              }]);
                            } else {
                              setSelectedAddOnCategories(prev => prev.filter(cat => cat.categoryId !== category.id));
                            }
                          }}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 dark:border-gray-600 rounded mt-1"
                        />
                        
                        <div className="flex-1">
                          <label htmlFor={`category-${category.id}`} className="flex items-center space-x-2 cursor-pointer">
                            <span className="font-medium text-gray-900 dark:text-white">{category.name}</span>
                            {category.isRequired && (
                              <span className="px-2 py-1 bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-200 text-xs rounded-full">
                                Required
                              </span>
                            )}
                          </label>
                          
                          {category.description && (
                            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{category.description}</p>
                          )}
                          
                          <div className="flex items-center space-x-4 mt-2 text-xs text-gray-500 dark:text-gray-400">
                            <span>Min: {category.minSelections}</span>
                            <span>Max: {category.maxSelections === 0 ? 'Unlimited' : category.maxSelections}</span>
                          </div>
                        </div>
                      </div>
                      
                      {/* Category-specific settings when selected */}
                      {isSelected && selectedCategory && (
                        <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700 space-y-3">
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                            <div>
                              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                                Required for this item
                              </label>
                              <input
                                type="checkbox"
                                checked={selectedCategory.isRequired}
                                onChange={(e) => {
                                  setSelectedAddOnCategories(prev => prev.map(cat => 
                                    cat.categoryId === category.id 
                                      ? { ...cat, isRequired: e.target.checked }
                                      : cat
                                  ));
                                }}
                                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 dark:border-gray-600 rounded"
                              />
                            </div>
                            
                            <div>
                              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                                Min Selections
                              </label>
                              <input
                                type="number"
                                min="0"
                                max={category.maxSelections || 999}
                                value={selectedCategory.minSelections}
                                onChange={(e) => {
                                  setSelectedAddOnCategories(prev => prev.map(cat => 
                                    cat.categoryId === category.id 
                                      ? { ...cat, minSelections: parseInt(e.target.value) || 0 }
                                      : cat
                                  ));
                                }}
                                className="input-field text-sm"
                              />
                            </div>
                            
                            <div>
                              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                                Max Selections
                              </label>
                              <input
                                type="number"
                                min={selectedCategory.minSelections}
                                value={selectedCategory.maxSelections}
                                onChange={(e) => {
                                  setSelectedAddOnCategories(prev => prev.map(cat => 
                                    cat.categoryId === category.id 
                                      ? { ...cat, maxSelections: parseInt(e.target.value) || 0 }
                                      : cat
                                  ));
                                }}
                                className="input-field text-sm"
                              />
                            </div>
                          </div>
                          
                          <div>
                            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                              Display Order
                            </label>
                            <input
                              type="number"
                              min="0"
                              value={selectedCategory.displayOrder}
                              onChange={(e) => {
                                setSelectedAddOnCategories(prev => prev.map(cat => 
                                  cat.categoryId === category.id 
                                    ? { ...cat, displayOrder: parseInt(e.target.value) || 0 }
                                    : cat
                                ));
                              }}
                              className="input-field text-sm"
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex space-x-4 pt-6 border-t border-gray-200 dark:border-gray-700">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {loading ? 'Adding...' : 'Add Menu Item'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default AddMenuItemModal; 